import type {
  AccountRecord,
  MembershipRecord
} from '@libs/data-access/entities'
import { useHover } from '@libs/ui/ds'
import type { LabelModel } from '@spa/scenes/Plannings/redux/models/Label'
import type { LocationModel } from '@spa/scenes/Plannings/redux/models/Location'
import type { RestModel } from '@spa/scenes/Plannings/redux/models/Rest'
import type { ShiftModel } from '@spa/scenes/Plannings/redux/models/Shift'
import type { TeamModel } from '@spa/scenes/Plannings/redux/models/Team'
import type { WeeklyscheduleModel } from '@spa/scenes/Plannings/redux/models/Weeklyschedule'
import React, { useMemo } from 'react'
import type { ReactNode, FC } from 'react'

import { CardInfo } from '../../../Card'

import RestDetails from './RestDetails/RestDetails'
import styles from './ShiftCard.module.css'
import ShiftDuration from './ShiftDuration/ShiftDuration'
import ShiftFooter from './ShiftFooter/ShiftFooter'
import ShiftChecked from './ShiftFooterItems/ShiftChecked'
import ShiftEditedByEmployee from './ShiftFooterItems/ShiftEditedByEmployee'
import ShiftMeals from './ShiftFooterItems/ShiftMeals'
import ShiftNote from './ShiftFooterItems/ShiftNote'
import ShiftUnplanned from './ShiftFooterItems/ShiftUnplanned'
import ShiftHours from './ShiftHours/ShiftHours'
import ShiftLabel from './ShiftLabel/ShiftLabel'
import ShiftTimeDifference from './ShiftTimeDifference/ShiftTimeDifference'
import ShiftWrapper from './ShiftWrapper/ShiftWrapper'
import isPast from './utils/isPast'

export type ShiftCardProps = {
  currentTeam: TeamModel
  shift: ShiftModel
  rest: RestModel
  label: LabelModel
  membership: MembershipRecord
  account: AccountRecord
  shiftTeam: TeamModel
  shiftLocation: LocationModel
  shiftWeeklyschedule: WeeklyscheduleModel
  content?: ReactNode
  title?: ReactNode
  outlined?: boolean
  disabled?: boolean
  leftSection?: ReactNode
  classNames?: {
    root?: string
    title?: string
  }
}
const ShiftCard: FC<ShiftCardProps> = ({
  currentTeam,
  shift,
  rest,
  label,
  membership,
  account,
  shiftTeam,
  shiftLocation,
  shiftWeeklyschedule,
  content,
  leftSection,
  title,
  outlined,
  disabled,
  classNames = {}
}) => {
  const { ref, hovered } = useHover()

  const type = useMemo(() => {
    const past = isPast(shift.starts_at)
    const published = !!shiftWeeklyschedule?.published
    const updatedByEmployee = !!shift.real_hours_last_modified_by_employee_at

    return (
      ((!!shift.rest_id || shift.did_not_show) && 'gray') ||
      (updatedByEmployee && 'dark') ||
      (past && published && 'smooth') ||
      (past && 'light') ||
      null
    )
  }, [
    shift.starts_at,
    shift.rest_id,
    shift.did_not_show,
    shiftWeeklyschedule?.published,
    shift.real_hours_last_modified_by_employee_at
  ])

  return (
    <div ref={ref} data-testid='shift-card' className={classNames.root}>
      <ShiftWrapper
        disabled={disabled}
        outlined={outlined}
        shift={shift}
        shiftTeam={shiftTeam}
        team={currentTeam}
        label={label}
      >
        <div className={styles.ContentWrapper}>
          {Boolean(leftSection) && leftSection}
          <ShiftLabel
            label={label}
            title={title}
            className={classNames.title}
          />
          {/* @ts-ignore TO DO: fix (strictNullChecks errors) (https://snapshiftapp.atlassian.net/browse/COP-333) */}
          <CardInfo.Container type={type} color={label?.color}>
            {shift.did_not_show && <RestDetails rest={rest} />}
            {!shift.did_not_show && !hovered && <ShiftHours shift={shift} />}
            {!shift.did_not_show && hovered && (
              <ShiftDuration shift={shift} rest={rest} account={account} />
            )}

            <ShiftTimeDifference
              shift={shift}
              rest={rest}
              account={account}
              label={label}
            />
          </CardInfo.Container>
          <ShiftFooter
            team={currentTeam}
            shiftTeam={shiftTeam}
            shiftLocation={shiftLocation}
            membership={membership}
          >
            <ShiftEditedByEmployee shift={shift} />
            <ShiftMeals shift={shift} />
            <ShiftNote shift={shift} rest={rest} />
            <ShiftUnplanned shift={shift} />
            <ShiftChecked shift={shift} />
          </ShiftFooter>
        </div>
        {content}
      </ShiftWrapper>
    </div>
  )
}

export default ShiftCard
